import type { WorkflowFile } from '@/interfaces/workflows.interface';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useReducer } from 'react';

// State interface for file management
interface WorkflowFilesState {
  files: WorkflowFile[];
  expandedFolders: string[];
  pickOnlyFile: WorkflowFile | null;
}

// Actions for file management
type WorkflowFilesAction =
  | { type: 'ADD_FILE'; payload: WorkflowFile }
  | { type: 'ADD_FILES'; payload: WorkflowFile[] }
  | { type: 'REMOVE_FILE'; payload: string }
  | { type: 'REMOVE_FOLDER'; payload: string }
  | {
      type: 'UPDATE_FILE';
      payload: { fileId: string; updates: Partial<WorkflowFile> };
    }
  | {
      type: 'MOVE_FILE';
      payload: { fileId: string; newFolderPath: string };
    }
  | {
      type: 'MOVE_FOLDER';
      payload: { folderPath: string; newParentPath: string };
    }
  | {
      type: 'RENAME_FILE';
      payload: { fileId: string; newName: string };
    }
  | {
      type: 'RENAME_FOLDER';
      payload: { folderPath: string; newName: string };
    }
  | { type: 'TOGGLE_EXPANDED_FOLDER'; payload: string }
  | { type: 'SET_EXPANDED_FOLDERS'; payload: string[] }
  | { type: 'CLEAR_EXPANDED_FOLDERS' }
  | { type: 'CLEAR_FILES' }
  | { type: 'UPDATE_PICK_ONLY_FILE'; payload: WorkflowFile | null };

// Initial state
const initialState: WorkflowFilesState = {
  files: [],
  expandedFolders: [],
  pickOnlyFile: null,
};

const mergeFileProperties = (
  existingFile: WorkflowFile,
  newFile: WorkflowFile
): WorkflowFile => {
  const size =
    newFile.size ||
    existingFile.size ||
    newFile.fileBlob?.size ||
    existingFile.fileBlob?.size ||
    0;
  return {
    ...existingFile,
    ...newFile,
    size,
  };
};

/**
 * Whether two entries describe one file.
 *
 * A server id settles it outright. Otherwise the name within one folder does,
 * and "one folder" has to survive the two ways the root is written: the
 * uploader stores a root file with `parentId: null`, the API listing with
 * `parentId: undefined`. Compared with `===` they never matched, and one
 * upload was kept twice — once with its storage path, once without.
 */
const isSameFileEntry = (a: WorkflowFile, b: WorkflowFile): boolean => {
  if (a.id === b.id) return true;
  // Two projects can each hold a report.csv at their root. Merging those would
  // carry one project's storage path and statistics onto the other's file, so
  // the name only settles it for entries no owner tells apart.
  if (a.projectId !== undefined && b.projectId !== undefined && a.projectId !== b.projectId) {
    return false;
  }
  return a.name === b.name && (a.parentId ?? null) === (b.parentId ?? null);
};

// Reducer for file management
const workflowFilesReducer = (
  state: WorkflowFilesState,
  action: WorkflowFilesAction
): WorkflowFilesState => {
  switch (action.type) {
    case 'ADD_FILE': {
      const existingFileIndex = state.files.findIndex((file) =>
        isSameFileEntry(file, action.payload)
      );

      if (existingFileIndex !== -1) {
        // Merge properties, giving precedence to the new file
        const existingFile = state.files[existingFileIndex];
        const mergedFile = mergeFileProperties(existingFile, action.payload);

        const updatedFiles = [...state.files];
        updatedFiles[existingFileIndex] = mergedFile;
        return {
          ...state,
          files: updatedFiles,
        };
      } else {
        // Add the new file if no duplicate exists
        return {
          ...state,
          files: [...state.files, action.payload],
        };
      }
    }
    case 'ADD_FILES': {
      const newFiles = action.payload;
      const updatedFiles = [...state.files];

      newFiles.forEach((newFile) => {
        const existingFileIndex = updatedFiles.findIndex((file) =>
          isSameFileEntry(file, newFile)
        );

        if (existingFileIndex !== -1) {
          // Merge properties, giving precedence to the new file
          const existingFile = updatedFiles[existingFileIndex];
          const mergedFile = mergeFileProperties(existingFile, newFile);
          updatedFiles[existingFileIndex] = mergedFile;
        } else {
          // Add the new file if no duplicate exists
          updatedFiles.push(newFile);
        }
      });

      return {
        ...state,
        files: updatedFiles,
      };
    }
    case 'REMOVE_FILE':
      return {
        ...state,
        files: state.files.filter((file) => file.id !== action.payload),
      };
    case 'REMOVE_FOLDER':
      return {
        ...state,
        files: state.files.filter((file) => {
          // Remove the folder itself
          if (file.id === action.payload) {
            return false;
          }

          // Remove files that have this folder as their parent
          if (file.parentId === action.payload) {
            return false;
          }

          return true; // Keep all other files
        }),
      };
    case 'UPDATE_FILE':
      return {
        ...state,
        files: state.files.map((file) =>
          file.id === action.payload.fileId
            ? { ...file, ...action.payload.updates }
            : file
        ),
      };
    case 'MOVE_FILE':
      return {
        ...state,
        files: state.files.map((file) =>
          file.id === action.payload.fileId
            ? { ...file, parentId: action.payload.newFolderPath }
            : file
        ),
      };
    case 'MOVE_FOLDER':
      return {
        ...state,
        files: state.files.map((file) => {
          // Move the folder itself
          if (file.id === action.payload.folderPath) {
            return { ...file, parentId: action.payload.newParentPath };
          }

          // Move files that have this folder as their parent
          if (file.parentId === action.payload.folderPath) {
            return { ...file, parentId: action.payload.newParentPath };
          }

          return file;
        }),
      };
    case 'RENAME_FILE':
      return {
        ...state,
        files: state.files.map((file) =>
          file.id === action.payload.fileId
            ? { ...file, name: action.payload.newName }
            : file
        ),
      };
    case 'RENAME_FOLDER':
      return {
        ...state,
        files: state.files.map((file) => {
          // Handle API-based folder structure (folders created via API)
          if (
            file.type === 'folder' &&
            file.isFolder &&
            file.id === action.payload.folderPath
          ) {
            return {
              ...file,
              name: action.payload.newName,
            };
          }

          return file;
        }),
      };
    case 'TOGGLE_EXPANDED_FOLDER':
      return {
        ...state,
        expandedFolders: state.expandedFolders.includes(action.payload)
          ? state.expandedFolders.filter((path) => path !== action.payload)
          : [...state.expandedFolders, action.payload],
      };
    case 'SET_EXPANDED_FOLDERS':
      return {
        ...state,
        expandedFolders: action.payload,
      };
    case 'CLEAR_EXPANDED_FOLDERS':
      return {
        ...state,
        expandedFolders: [],
      };
    case 'CLEAR_FILES':
      return {
        ...state,
        files: [],
      };
    case 'UPDATE_PICK_ONLY_FILE':
      return {
        ...state,
        pickOnlyFile: action.payload,
      };
    default:
      return state;
  }
};

// Context interface
interface WorkflowFilesContextType {
  state: WorkflowFilesState;
  addFile: (file: WorkflowFile) => void;
  addFiles: (files: WorkflowFile[]) => void;
  removeFile: (fileId: string) => void;
  removeFolder: (folderPath: string) => void;
  updateFile: (fileId: string, updates: Partial<WorkflowFile>) => void;
  moveFile: (fileId: string, newFolderPath: string) => void;
  moveFolder: (folderPath: string, newParentPath: string) => void;
  renameFile: (fileId: string, newName: string) => void;
  renameFolder: (folderPath: string, newName: string) => void;
  // Expanded folders management
  toggleExpandedFolder: (folderPath: string) => void;
  setExpandedFolders: (folderPaths: string[]) => void;
  clearExpandedFolders: () => void;
  clearFiles: () => void;
  updatePickOnlyFile: (file: WorkflowFile | null) => void;
}

// Create context
const WorkflowFilesContext = createContext<
  WorkflowFilesContextType | undefined
>(undefined);

// Provider component
interface WorkflowFilesProviderProps {
  children: ReactNode;
  /**
   * The project whose files this collection holds. An upload arrives without
   * one — the uploader knows the folder, not the project — so entries are
   * stamped on the way in. Without that, a file could not be told apart from
   * another project's, which is what decides whether it is safe to keep on a
   * consumer's first mount and whether it belongs in a project's own lists.
   */
  projectId?: string | null;
}

export const WorkflowFilesProvider: React.FC<WorkflowFilesProviderProps> = ({
  children,
  projectId,
}) => {
  const [state, dispatch] = useReducer(workflowFilesReducer, initialState);

  /** Records who an entry belongs to, where it does not already say. */
  const owned = (file: WorkflowFile): WorkflowFile =>
    file.projectId === undefined && projectId ? { ...file, projectId } : file;

  const addFile = (file: WorkflowFile) => {
    dispatch({ type: 'ADD_FILE', payload: owned(file) });
  };

  const addFiles = (files: WorkflowFile[]) => {
    dispatch({ type: 'ADD_FILES', payload: files.map(owned) });
  };

  const removeFile = (fileId: string) => {
    dispatch({ type: 'REMOVE_FILE', payload: fileId });
  };

  const removeFolder = (folderPath: string) => {
    dispatch({ type: 'REMOVE_FOLDER', payload: folderPath });
  };

  const updateFile = (fileId: string, updates: Partial<WorkflowFile>) => {
    dispatch({ type: 'UPDATE_FILE', payload: { fileId, updates } });
  };

  const moveFile = (fileId: string, newFolderPath: string) => {
    dispatch({ type: 'MOVE_FILE', payload: { fileId, newFolderPath } });
  };

  const moveFolder = (folderPath: string, newParentPath: string) => {
    dispatch({ type: 'MOVE_FOLDER', payload: { folderPath, newParentPath } });
  };

  const renameFile = (fileId: string, newName: string) => {
    dispatch({ type: 'RENAME_FILE', payload: { fileId, newName } });
  };

  const renameFolder = (folderPath: string, newName: string) => {
    dispatch({ type: 'RENAME_FOLDER', payload: { folderPath, newName } });
  };

  const toggleExpandedFolder = (folderPath: string) => {
    dispatch({ type: 'TOGGLE_EXPANDED_FOLDER', payload: folderPath });
  };

  const setExpandedFolders = (folderPaths: string[]) => {
    dispatch({ type: 'SET_EXPANDED_FOLDERS', payload: folderPaths });
  };

  const clearExpandedFolders = () => {
    dispatch({ type: 'CLEAR_EXPANDED_FOLDERS' });
  };

  const clearFiles = () => {
    dispatch({ type: 'CLEAR_FILES' });
  };

  const updatePickOnlyFile = (file: WorkflowFile | null) => {
    dispatch({ type: 'UPDATE_PICK_ONLY_FILE', payload: file });
  };

  const value: WorkflowFilesContextType = {
    state,
    addFile,
    addFiles,
    removeFile,
    removeFolder,
    updateFile,
    moveFile,
    moveFolder,
    renameFile,
    renameFolder,
    toggleExpandedFolder,
    setExpandedFolders,
    clearExpandedFolders,
    clearFiles,
    updatePickOnlyFile,
  };

  return (
    <WorkflowFilesContext.Provider value={value}>
      {children}
    </WorkflowFilesContext.Provider>
  );
};

// Hook to use the context
export const useWorkflowFilesContext = () => {
  const context = useContext(WorkflowFilesContext);
  if (context === undefined) {
    throw new Error(
      'useWorkflowFilesContext must be used within a WorkflowFilesProvider'
    );
  }
  return context;
};
